import * as L from 'leaflet';
import {
  EMPTY,
  EmptyError,
  Observable,
  Subject,
  filter,
  firstValueFrom,
  map,
  merge,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';

export type QuestionWmsPane = 'left' | 'right';

interface HandoffEvent {
  sensorId: unknown;
  sceneId?: unknown;
  pane: QuestionWmsPane;
}

/** Matches question-tasking feed loads to the imagery layer created for their scene and pane. */
export class QuestionWmsHandoffController {
  private readonly ready$ = new Subject<HandoffEvent & { layer: L.Layer }>();
  private readonly failed$ = new Subject<HandoffEvent & { error: unknown }>();
  private readonly cleared$ = new Subject<unknown>();
  private readonly destroyed$ = new Subject<void>();
  private readonly layers = new Map<string, L.Layer>();
  private readonly latestLayers = new Map<string, L.Layer>();
  private readonly failures = new Map<string, unknown>();
  private destroyed = false;

  private key(sensorId: unknown, sceneId: unknown, pane: QuestionWmsPane): string {
    return `${String(sensorId ?? '')}::${String(sceneId ?? '*')}::${pane}`;
  }

  private paneKey(sensorId: unknown, pane: QuestionWmsPane): string {
    return `${String(sensorId ?? '')}::${pane}`;
  }

  private sceneId(scene: any): unknown {
    return scene?.id ?? scene?.sceneId;
  }

  currentLayer(sensorId: unknown, sceneId: unknown, pane: QuestionWmsPane): L.Layer | undefined {
    return sceneId == null
      ? this.latestLayers.get(this.paneKey(sensorId, pane))
      : this.layers.get(this.key(sensorId, sceneId, pane));
  }

  publishLayer(scene: any, sensorId: unknown, layer: L.Layer, pane: QuestionWmsPane): void {
    if (this.destroyed) return;
    const sceneId = this.sceneId(scene);
    const key = this.key(sensorId, sceneId, pane);
    const paneKey = this.paneKey(sensorId, pane);
    this.failures.delete(key);
    this.layers.set(key, layer);
    this.latestLayers.set(paneKey, layer);
    layer.once('remove', () => {
      if (this.layers.get(key) !== layer) return;
      this.layers.delete(key);
      if (this.latestLayers.get(paneKey) === layer) this.latestLayers.delete(paneKey);
      this.publishFailure(scene, sensorId, pane, new Error('WMS layer was removed before the feed could load'));
    });
    this.ready$.next({ sensorId, sceneId, pane, layer });
  }

  publishFailure(scene: any, sensorId: unknown, pane: QuestionWmsPane, error: unknown): void {
    if (this.destroyed) return;
    const sceneId = this.sceneId(scene);
    this.failures.set(this.key(sensorId, sceneId, pane), error);
    this.failed$.next({ sensorId, sceneId, pane, error });
  }

  clear(sensorId: unknown): void {
    if (this.destroyed) return;
    this.cleared$.next(sensorId);
    const prefix = `${String(sensorId ?? '')}::`;
    for (const entries of [this.layers, this.latestLayers, this.failures]) {
      for (const key of entries.keys()) {
        if (key.startsWith(prefix)) entries.delete(key);
      }
    }
  }

  async waitForLayer(
    sensorId: unknown,
    sceneId: unknown,
    pane: QuestionWmsPane,
    cancel$: Observable<unknown> = EMPTY
  ): Promise<L.Layer> {
    if (this.destroyed) throw new EmptyError();
    const existing = this.currentLayer(sensorId, sceneId, pane);
    if (existing) return existing;
    if (sceneId != null) {
      const key = this.key(sensorId, sceneId, pane);
      if (this.failures.has(key)) throw this.failures.get(key);
    }

    const matches = (event: HandoffEvent) =>
      String(event.sensorId ?? '') === String(sensorId ?? '') &&
      event.pane === pane &&
      (sceneId == null || String(event.sceneId ?? '') === String(sceneId));

    return firstValueFrom(merge(
      this.ready$.pipe(filter((event) => matches(event)), map((event) => event.layer)),
      this.failed$.pipe(filter((event) => matches(event)), switchMap((event) => { throw event.error; })),
      this.cleared$.pipe(
        filter((clearedSensorId) => String(clearedSensorId ?? '') === String(sensorId ?? '')),
        switchMap(() => { throw new EmptyError(); })
      )
    ).pipe(take(1), takeUntil(merge(cancel$, this.destroyed$))));
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.destroyed$.next();
    this.destroyed$.complete();
    this.ready$.complete();
    this.failed$.complete();
    this.cleared$.complete();
    this.layers.clear();
    this.latestLayers.clear();
    this.failures.clear();
  }
}
